import { useCallback, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@rapid/queries/queryClient";

import { AppRoutes } from "@rapid/routing/Routes";
import { Toast } from "@rapid/components/shared/Toast";
import { useCIAlerts } from "@rapid/hooks/useCIAlerts";
import { useAuth } from "@rapid/contexts/AuthContext";
import { useCheckoutCompletion } from "@rapid/hooks/useCheckoutCompletion";
import LoginModal from "./components/auth/LoginModal";
import { RapidProviderStack } from "@rapid/providers/RapidProviderStack";
import { SharedAnalyticsProvider } from "@scripts/react/hooks/AnalyticsContext";
import { useAnalytics as useRapidAnalytics } from "@rapid/contexts/AnalyticsContext";
import type { AnalyticsEvent } from "@rapid/analytics/types";

export { RapidExperimentProvider } from "@rapid/providers/RapidAppProviders";
export { RapidActivitiesProvider } from "@rapid/providers/RapidAppProviders";

/**
 * Bridges Rapid's useAnalytics() into the shared analytics context
 * so cross-app components can call useSharedAnalytics().track().
 *
 * Must be rendered inside Rapid's AnalyticsProvider.
 */
function RapidSharedAnalyticsProvider({ children }: { children: React.ReactNode }) {
    const { track: rapidTrack } = useRapidAnalytics();

    const track = useCallback(
        (event: string, data?: Record<string, unknown>) => {
            rapidTrack({ event: event as AnalyticsEvent, data });
        },
        [rapidTrack]
    );

    return <SharedAnalyticsProvider track={track}>{children}</SharedAnalyticsProvider>;
}

export function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <RapidProviderStack queryClient={queryClient}>
                    <RapidSharedAnalyticsProvider>
                        <AppContent />
                    </RapidSharedAnalyticsProvider>
                </RapidProviderStack>
            </BrowserRouter>
        </QueryClientProvider>
    );
}

const AppContent = () => {
    const { isLoginModalOpen, closeLoginModal, guestAuthInitialized, initializeGuestAuth } =
        useAuth();

    useEffect(() => {
        if (!guestAuthInitialized) {
            initializeGuestAuth();
        }
    }, [guestAuthInitialized, initializeGuestAuth]);

    useCheckoutCompletion();
    useCIAlerts();

    return (
        <>
            <AppRoutes />
            <Toast />
            <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
        </>
    );
};
